/**
 * OpenCodeEventSource — turns the Conveyor plugin's bus-event sink into
 * HarnessEvents for a PTY session.
 *
 * Stateful where the pure mappers in ./events.ts cannot be:
 *   - roles: parts carry no author role; `message.updated` (emitted BEFORE a
 *     message's parts) does. Only assistant-authored parts become events, or
 *     the user's own pasted prompt would echo into card chat.
 *   - turn boundaries: the bus has no `result` record. A turn is
 *     busy → … → idle (`session.status`, plus a separate `session.idle` on
 *     1.18.4 — either counts, and a turn ends at most once); on idle a
 *     `result` is SYNTHESIZED from the accumulated step-finish usage and
 *     assistant text, which is what lets PtySession's existing
 *     result-ends-turn machinery run unchanged.
 *   - session identity: the first event latches opencode's `ses_…` id so the
 *     session can report it (synthetic `system init`) and later spawns can
 *     `--session` back into the same conversation.
 */

import type { PtyChatEventPayload } from "@project/shared";
import type { HarnessEvent } from "../types.js";
import {
  accumulateUsage,
  buildResultEvent,
  busInfoOf,
  busPartOf,
  busSessionIdOf,
  isBusyEvent,
  isIdleEvent,
  mapOpenCodeEvent,
  stringifyToolOutput,
  type OpenCodeBusEvent,
  type OpenCodeUsage,
} from "./events.js";

/** Bound on the messageID→role map; a session's live window never needs more. */
const MAX_TRACKED_MESSAGES = 200;
const CHAT_TEXT_MAX = 16_000;
const CHAT_TOOL_INPUT_MAX = 1_900;
const CHAT_TOOL_OUTPUT_MAX = 1_900;

function truncate(text: string, max: number): string {
  return text.length > max ? `${text.slice(0, max)}…` : text;
}

function compactJson(value: unknown): string {
  try {
    return JSON.stringify(value ?? {}).slice(0, CHAT_TOOL_INPUT_MAX);
  } catch {
    return String(value).slice(0, CHAT_TOOL_INPUT_MAX);
  }
}

function partString(part: Record<string, unknown>, key: string): string | null {
  const value = part[key];
  return typeof value === "string" && value.length > 0 ? value : null;
}

type OpenCodeBusPart = NonNullable<ReturnType<typeof busPartOf>>;
type RelayedToolStatus = "running" | "completed" | "error";

function relayedToolStatus(status: string | undefined): RelayedToolStatus | null {
  return status === "running" || status === "completed" || status === "error" ? status : null;
}

export class OpenCodeEventSource {
  private readonly roles = new Map<string, string>();
  private usage: OpenCodeUsage = { inputTokens: 0, outputTokens: 0, totalCostUsd: 0 };
  private assistantText = "";
  /** Busy has been seen and idle has not — a turn is in flight on the bus. */
  private active = false;
  private latchedSessionId: string | null = null;
  private readonly relayedTextParts = new Set<string>();
  private readonly relayedToolUses = new Set<string>();
  private readonly relayedToolResults = new Set<string>();

  constructor(
    private readonly emit: (event: HarnessEvent) => void,
    private readonly onSessionId?: (id: string) => void,
    private readonly emitChat?: (event: PtyChatEventPayload) => void,
  ) {}

  /** The opencode-assigned session id, once any event has carried it. */
  get sessionId(): string | null {
    return this.latchedSessionId;
  }

  /**
   * Ingest one parsed sink record. Self-arming: `busy` opens a turn (resetting
   * the accumulators) whether it came from our pasted prompt or a human typing
   * into the parked TUI — the passive case is exactly why turn state cannot
   * only reset from beginTurn.
   */
  handleRecord(raw: unknown): void {
    if (typeof raw !== "object" || raw === null) return;
    const event = raw as OpenCodeBusEvent;
    this.latchSessionId(event);
    const info = busInfoOf(event);
    if (info?.id && info.role) this.trackRole(info.id, info.role);
    if (isBusyEvent(event)) {
      if (!this.active) this.beginBusTurn();
      return;
    }
    if (isIdleEvent(event)) {
      this.finishBusTurn();
      return;
    }
    if (event.type === "session.error") {
      this.finishBusTurn(describeSessionError(event));
      return;
    }
    this.handlePart(event);
  }

  private handlePart(event: OpenCodeBusEvent): void {
    const part = busPartOf(event);
    if (!part) return;
    // A part can arrive before any session.status (observed: the assistant's
    // first parts race the busy flip) — treat it as turn-opening too.
    if (!this.active) this.beginBusTurn();
    if (typeof part.messageID !== "string") return;
    const role = this.roles.get(part.messageID);
    if (role === "user") {
      this.relayTextPart(part, "user_text");
      return;
    }
    if (role !== "assistant") return;
    this.relayAssistantPart(part);
    accumulateUsage({ part }, this.usage);
    const mapped = mapOpenCodeEvent({ part });
    if (!mapped) return;
    this.emit(mapped);
    if (mapped.type === "assistant") {
      const block = mapped.message.content[0];
      if (block?.type === "text" && block.text) this.assistantText += block.text;
    }
  }

  private beginBusTurn(): void {
    this.active = true;
    this.usage = { inputTokens: 0, outputTokens: 0, totalCostUsd: 0 };
    this.assistantText = "";
    this.relayedTextParts.clear();
    this.relayedToolUses.clear();
    this.relayedToolResults.clear();
  }

  /** Idle (or an error) closes the turn exactly once. */
  private finishBusTurn(error?: string | null): void {
    if (!this.active) return;
    this.active = false;
    this.emit(
      error
        ? buildResultEvent(1, this.usage, "", "", error)
        : buildResultEvent(0, this.usage, this.assistantText.trim(), ""),
    );
    this.emitChat?.({ kind: "turn_end" });
  }

  private relayAssistantPart(part: OpenCodeBusPart): void {
    if (part.type === "text") {
      this.relayTextPart(part, "assistant_text");
      return;
    }
    if (part.type === "tool") this.relayToolPart(part);
  }

  private relayToolPart(part: OpenCodeBusPart): void {
    const status = relayedToolStatus(part.state?.status);
    if (!status) return;
    const callId = truncate(partString(part, "callID") ?? partString(part, "id") ?? "", 100);
    const key = callId || `${part.messageID}:${partString(part, "tool") ?? "unknown"}`;
    this.relayToolUse(part, key, callId);
    if (status !== "running") this.relayToolResult(part, status, key, callId);
  }

  private relayToolUse(part: OpenCodeBusPart, key: string, callId: string): void {
    if (this.relayedToolUses.has(key)) return;
    this.relayedToolUses.add(key);
    this.emitChat?.({
      kind: "tool_use",
      name: truncate(partString(part, "tool") ?? "unknown", 200),
      input: compactJson(part.state?.input),
      ...(callId ? { id: callId } : {}),
    });
  }

  private relayToolResult(
    part: OpenCodeBusPart,
    status: Exclude<RelayedToolStatus, "running">,
    key: string,
    callId: string,
  ): void {
    if (this.relayedToolResults.has(key)) return;
    this.relayedToolResults.add(key);
    this.emitChat?.({
      kind: "tool_result",
      ...(callId ? { toolUseId: callId } : {}),
      output: truncate(
        stringifyToolOutput(status === "error" ? part.state?.error : part.state?.output),
        CHAT_TOOL_OUTPUT_MAX,
      ),
      isError: status === "error",
    });
  }

  private relayTextPart(part: OpenCodeBusPart, kind: "user_text" | "assistant_text"): void {
    const text = typeof part.text === "string" ? part.text : "";
    if (text.trim() === "") return;
    const key = partString(part, "id") ?? `${part.messageID}:${kind}`;
    if (this.relayedTextParts.has(key)) return;
    this.relayedTextParts.add(key);
    this.emitChat?.({
      kind,
      text: truncate(kind === "user_text" ? text.trim() : text, CHAT_TEXT_MAX),
    });
  }

  private latchSessionId(event: OpenCodeBusEvent): void {
    if (this.latchedSessionId) return;
    const id = busSessionIdOf(event);
    if (!id) return;
    this.latchedSessionId = id;
    this.onSessionId?.(id);
  }

  private trackRole(id: string, role: string): void {
    this.roles.set(id, role);
    if (this.roles.size > MAX_TRACKED_MESSAGES) {
      const oldest = this.roles.keys().next().value;
      if (oldest !== undefined) this.roles.delete(oldest);
    }
  }
}

/** Best-effort message for a `session.error` bus event. */
function describeSessionError(event: OpenCodeBusEvent): string {
  const error = (event.properties as { error?: { message?: unknown } } | undefined)?.error;
  if (error && typeof error.message === "string") return error.message;
  return `opencode reported a session error: ${JSON.stringify(event.properties ?? {}).slice(0, 300)}`;
}
